import { PlayyardSpookyTree } from "./halloween/PlayyardSpookyTree";
import { Column } from "@/components/Column";

import { IS_PLAYYARD_HALLOWEEN } from "@/lib/constants";
import { playyardSky } from "@/lib/playyardSky";

const TUFT_COUNT = 46;
const GROUND_TOP_PERCENT = 55;
const FLOWER_COLORS = ["#ffffff", "#ffd84a", "#ff8fb1", "#b69cff"];
// Tufts and flowers stay out of the pond (left side, just below the ridge).
const POND_BOUNDS = { left: 0, right: 33, top: 55, bottom: 73 };
const TREES = [
  { left: 36, top: 55, sizeCqw: 4 },
  { left: 41, top: 56, sizeCqw: 5.5 },
  { left: 46, top: 55.5, sizeCqw: 3.5 },
  { left: 62, top: 57, sizeCqw: 3 },
  { left: 88, top: 54, sizeCqw: 5 },
  { left: 93, top: 55, sizeCqw: 3.8 },
];

const isInPond = (left: number, top: number) =>
  left > POND_BOUNDS.left &&
  left < POND_BOUNDS.right &&
  top > POND_BOUNDS.top &&
  top < POND_BOUNDS.bottom;

const TUFTS = Array.from({ length: TUFT_COUNT }, (_, index) => {
  const top =
    GROUND_TOP_PERCENT +
    2 +
    playyardSky.random(index + 301) * (100 - GROUND_TOP_PERCENT - 3);
  const left = playyardSky.random(index + 401) * 100;
  // Nearer tufts (lower on screen) draw bigger.
  const depth = (top - GROUND_TOP_PERCENT) / (100 - GROUND_TOP_PERCENT);
  return {
    top,
    left,
    widthCqw: 0.8 + depth * 1.8,
    delayS: -playyardSky.random(index + 501) * 3,
    hasFlower: index % 3 === 0,
    flowerColor: FLOWER_COLORS[index % FLOWER_COLORS.length],
  };
}).filter((tuft) => !isInPond(tuft.left, tuft.top));

export const PlayyardLandLayer = () => {
  return (
    <Column className="pointer-events-none absolute inset-0">
      <svg
        className="absolute left-0 top-[30%] h-[30%] w-full"
        viewBox="0 0 100 30"
        preserveAspectRatio="none"
      >
        <path
          style={{ fill: "var(--yard-farHill)" }}
          d="M0 18 Q12 4 26 14 T52 10 T78 12 T100 6 V30 H0Z"
        />
      </svg>
      <svg
        className="absolute left-0 top-[40%] h-[20%] w-full"
        viewBox="0 0 100 20"
        preserveAspectRatio="none"
      >
        <path
          style={{ fill: "var(--yard-midHill)" }}
          d="M0 12 Q18 2 36 10 T70 8 T100 12 V20 H0Z"
        />
      </svg>
      {TREES.map((tree, index) =>
        IS_PLAYYARD_HALLOWEEN ? (
          <PlayyardSpookyTree key={index} {...tree} />
        ) : (
          <PlayyardTree key={index} {...tree} />
        ),
      )}
      <Column
        className="absolute inset-x-0 bottom-0"
        style={{
          top: `${GROUND_TOP_PERCENT.toString()}%`,
          background:
            "linear-gradient(var(--yard-groundTop), var(--yard-groundBottom))",
        }}
      >
        <Column className="absolute inset-0 bg-[repeating-linear-gradient(100deg,transparent_0_5cqw,rgb(255_255_255_/_0.035)_5cqw_10cqw)]" />
      </Column>
      {TUFTS.map((tuft, index) => (
        <PlayyardTuft key={index} {...tuft} />
      ))}
    </Column>
  );
};

const PlayyardTree = ({
  left,
  top,
  sizeCqw,
}: {
  left: number;
  top: number;
  sizeCqw: number;
}) => {
  return (
    <Column
      className="absolute -translate-x-1/2 -translate-y-full"
      style={{
        left: `${left.toString()}%`,
        top: `${top.toString()}%`,
        width: `${sizeCqw.toString()}cqw`,
        height: `${(sizeCqw * 1.4).toString()}cqw`,
      }}
    >
      <Column className="absolute bottom-0 left-[44%] h-[40%] w-[12%] bg-[#3b2a22]" />
      <Column
        className="absolute inset-x-0 bottom-[28%] top-0 rounded-[50%_50%_45%_45%]"
        style={{
          background:
            "radial-gradient(circle at 35% 30%, color-mix(in srgb, var(--yard-midHill) 80%, white), var(--yard-nearHill) 70%)",
        }}
      />
    </Column>
  );
};

const PlayyardTuft = ({
  top,
  left,
  widthCqw,
  delayS,
  hasFlower,
  flowerColor,
}: {
  top: number;
  left: number;
  widthCqw: number;
  delayS: number;
  hasFlower: boolean;
  flowerColor: string;
}) => {
  return (
    <>
      <Column
        // Sway owns `transform`, so the tuft anchors by bottom/margin instead.
        className="staffer-pet-yard-sway absolute [clip-path:polygon(0_100%,15%_35%,30%_100%,45%_0,60%_100%,75%_30%,90%_100%)]"
        style={{
          bottom: `${(100 - top).toString()}%`,
          left: `${left.toString()}%`,
          marginLeft: `${(-widthCqw / 2).toString()}cqw`,
          width: `${widthCqw.toString()}cqw`,
          height: `${(widthCqw * 0.8).toString()}cqw`,
          backgroundColor: "var(--yard-tuft)",
          animationDelay: `${delayS.toString()}s`,
        }}
      />
      {hasFlower ? (
        <Column
          className="absolute -translate-x-1/2 -translate-y-full rounded-full"
          style={{
            top: `${(top - 0.8).toString()}%`,
            left: `${(left + 1.2).toString()}%`,
            width: `${(widthCqw * 0.4).toString()}cqw`,
            height: `${(widthCqw * 0.4).toString()}cqw`,
            background: `radial-gradient(#ffd84a 0 30%, ${flowerColor} 32%)`,
          }}
        />
      ) : null}
    </>
  );
};
